import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import moment from 'moment';
import { ColumnButton } from 'lib/components/ColumnButton';
import {
  ElasticNoticeSearchResults,
  ElasticOrderSearchResults
} from 'lib/types/searchable';
import { Product } from 'lib/enums/Product';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { ColumnService } from 'lib/services/directory';
import { Alert } from 'lib/components/Alert/Alert';
import { useReportParams } from '../context/ReportParamsContext';
import { useReportsLoading } from '../context/LoadingContext';
import {
  fetchNoticesWithParams,
  fetchOrdersWithParams,
  fetchRevenueByMonthAggregation
} from '../utils/elastic';
import { exportSearchableRecordsToCSV } from '../utils/exportOrdersToCSV';
import {
  NEWSPAPER_ORDER_CSV_COLUMNS,
  REPORT_V2_NOTICE_CSV_COLUMNS
} from '../constants/csvColumns';

const ORDER_TYPE_COLORS: Record<string, string> = {
  [Product.Obituary]: '#2D9BDB',
  [Product.Classified]: '#00925E',
  notices: '#6388C1'
};

const ORDER_TYPE_LABELS: Record<string, string> = {
  [Product.Obituary]: 'Obituaries',
  [Product.Classified]: 'Classifieds',
  notices: 'Notices'
};

interface OrdersCalendarMonthBucket {
  key: string;
  orderType: {
    buckets: Array<{
      key: string;
      revenue: { value: number };
    }>;
  };
}

interface NoticesCalendarMonthBucket {
  key: string;
  revenue: { value: number };
}

export default function ProductRevenueBarChart() {
  const { getOrgIds, getDateRange } = useReportParams();
  const { dateRange, startDate, endDate } = getDateRange();
  const { withLoading } = useReportsLoading();

  const { value: aggregationData, error } = useAsyncEffect<{
    orders: ElasticOrderSearchResults;
    notices: ElasticNoticeSearchResults;
  } | null>({
    fetchData: () =>
      withLoading(fetchRevenueByMonthAggregation(getOrgIds, getDateRange)),
    initialData: null,
    dependencies: [getOrgIds().sort().join(','), dateRange, startDate, endDate],
    errorConfig: {
      service: ColumnService.ELASTIC,
      message:
        '[COLUMN REPORTS V2] Failed to fetch revenue aggregations for Products tab',
      tags: {
        dateRange,
        startDate: startDate || '',
        endDate: endDate || ''
      }
    }
  });

  const { labels, datasets } = useMemo(() => {
    const orderBuckets = aggregationData?.orders?.aggregations?.calendarMonth
      ?.buckets as OrdersCalendarMonthBucket[] | undefined;
    const noticeBuckets = aggregationData?.notices?.aggregations?.calendarMonth
      ?.buckets as NoticesCalendarMonthBucket[] | undefined;

    if (!orderBuckets || !noticeBuckets) {
      return { labels: [], datasets: [] };
    }

    const monthMap = new Map<
      string,
      {
        classifieds: number;
        obituaries: number;
        notices: number;
        monthKey: string;
      }
    >();

    orderBuckets.forEach(bucket => {
      const monthKey = bucket.key;
      const monthData = monthMap.get(monthKey) || {
        classifieds: 0,
        obituaries: 0,
        notices: 0,
        monthKey
      };

      bucket.orderType.buckets.forEach(orderTypeBucket => {
        const orderType = orderTypeBucket.key;
        const revenue = orderTypeBucket.revenue.value / 100;

        if (orderType === Product.Classified) {
          monthData.classifieds = revenue;
        } else if (orderType === Product.Obituary) {
          monthData.obituaries = revenue;
        }
      });

      monthMap.set(monthKey, monthData);
    });

    noticeBuckets.forEach(bucket => {
      const monthKey = bucket.key;
      const monthData = monthMap.get(monthKey) || {
        classifieds: 0,
        obituaries: 0,
        notices: 0,
        monthKey
      };

      monthData.notices = bucket.revenue.value / 100;
      monthMap.set(monthKey, monthData);
    });

    const sortedMonths = Array.from(monthMap.values()).sort(
      (a, b) => new Date(a.monthKey).getTime() - new Date(b.monthKey).getTime()
    );

    const months = sortedMonths.map(monthData =>
      moment.utc(monthData.monthKey).format('MMM YYYY')
    );

    const datasets = [
      {
        label: ORDER_TYPE_LABELS[Product.Classified],
        backgroundColor: ORDER_TYPE_COLORS[Product.Classified],
        data: sortedMonths.map(monthData => monthData.classifieds),
        stack: 'revenue'
      },
      {
        label: ORDER_TYPE_LABELS[Product.Obituary],
        backgroundColor: ORDER_TYPE_COLORS[Product.Obituary],
        data: sortedMonths.map(monthData => monthData.obituaries),
        stack: 'revenue'
      },
      {
        label: ORDER_TYPE_LABELS.notices,
        backgroundColor: ORDER_TYPE_COLORS.notices,
        data: sortedMonths.map(monthData => monthData.notices),
        stack: 'revenue'
      }
    ];

    return {
      labels: months,
      datasets
    };
  }, [aggregationData]);

  const options = {
    responsive: true,
    plugins: {
      legend: { position: 'bottom' as const },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label(context: any) {
            return `${context.dataset.label}: $${Number(
              context.parsed.y
            ).toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      x: { stacked: true },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          callback(value: string | number) {
            return `$${Number(value).toLocaleString()}`;
          }
        }
      }
    }
  };

  const handleExportCSV = async () => {
    const newspaperOrders = await fetchOrdersWithParams(
      getOrgIds,
      getDateRange,
      withLoading
    );

    const notices = await fetchNoticesWithParams(
      getOrgIds,
      getDateRange,
      withLoading
    );

    exportSearchableRecordsToCSV({
      dataAndColumns: [
        {
          data: newspaperOrders,
          columns: NEWSPAPER_ORDER_CSV_COLUMNS
        },
        {
          data: notices,
          columns: REPORT_V2_NOTICE_CSV_COLUMNS
        }
      ],
      filename: `mixed_records_export_${moment().format('YYYY-MM-DD')}.csv`
    });
  };

  if (error) {
    return (
      <Alert status="error" id="products-tab-error">
        Error: {error.message}
      </Alert>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-end mb-4">
        <ColumnButton
          id="export-csv-button"
          buttonText="Export CSV"
          onClick={handleExportCSV}
          primary
          type="button"
        />
      </div>
      <Bar data={{ labels, datasets }} options={options} />
    </div>
  );
}
